import { useEffect, useId, useRef, useState } from "react";

import ChevronRightIcon from "@/assets/icons/admin/chevron-right.svg?react";
import DotsVerticalIcon from "@/assets/icons/admin/dots-vertical.svg?react";
import EyeIcon from "@/assets/icons/admin/eye.svg?react";
import MailIcon from "@/assets/icons/admin/mail.svg?react";
import SearchIcon from "@/assets/icons/admin/search.svg?react";
import TrashIcon from "@/assets/icons/admin/trash.svg?react";
import {
  AdminLeadDetailModal,
  type AdminLeadDetail,
} from "@/components/admin/admin-lead-detail-modal";
import type { AdminScoreFilter } from "@/lib/admin-api";
import { composeLeadEmail } from "@/lib/admin-mailto";
import { cn } from "@/lib/utils";

export type AdminLeadRow = AdminLeadDetail & {
  totalDrag: string;
};

type AdminLeadsTableProps = {
  leads: AdminLeadRow[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  search: string;
  scoreFilter: AdminScoreFilter;
  isLoading?: boolean;
  title?: string;
  emptyMessage?: string;
  searchLabel?: string;
  paginationLabel?: string;
  onSearchChange: (value: string) => void;
  onScoreFilterChange: (value: AdminScoreFilter) => void;
  onPageChange: (page: number) => void;
  onDeleteLead?: (lead: AdminLeadRow) => void | Promise<void>;
};

const SCORE_FILTER_OPTIONS = [
  { value: "all", label: "All Scores" },
  { value: "low", label: "Low Risk (0 - 19)" },
  { value: "medium", label: "Medium Risk (20 - 59)" },
  { value: "high", label: "High Risk (60 - 100)" },
] as const satisfies ReadonlyArray<{
  value: AdminScoreFilter;
  label: string;
}>;

function getScoreFilterLabel(filter: AdminScoreFilter): string {
  return (
    SCORE_FILTER_OPTIONS.find((option) => option.value === filter)?.label ??
    "All Scores"
  );
}

function scoreBarClass(tone: AdminLeadRow["scoreTone"]): string {
  return tone === "critical" ? "bg-[#ef4444]" : "bg-[#eab308]";
}

export function AdminLeadsTable({
  leads,
  totalCount,
  currentPage,
  totalPages,
  search,
  scoreFilter,
  isLoading = false,
  title = "Total Leads",
  emptyMessage = "No leads match your filters.",
  searchLabel = "Search leads",
  paginationLabel = "Leads pagination",
  onSearchChange,
  onScoreFilterChange,
  onPageChange,
  onDeleteLead,
}: AdminLeadsTableProps) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [openActionId, setOpenActionId] = useState<string | null>(null);
  const [selectedLead, setSelectedLead] = useState<AdminLeadDetail | null>(
    null,
  );
  const filterRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const filterListId = useId();

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (filterRef.current && !filterRef.current.contains(target)) {
        setFilterOpen(false);
      }
      if (tableRef.current && !tableRef.current.contains(target)) {
        setOpenActionId(null);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  async function handleDelete(lead: AdminLeadRow) {
    setOpenActionId(null);
    setSelectedLead(null);
    await onDeleteLead?.(lead);
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <AdminLeadDetailModal
        lead={selectedLead}
        onClose={() => setSelectedLead(null)}
        onDelete={(lead) => handleDelete(lead as AdminLeadRow)}
        onSendMail={(lead) => {
          composeLeadEmail(lead.email);
        }}
      />
      <section
        ref={tableRef}
        className="overflow-hidden rounded-xl border border-white/5 bg-[#132136]"
      >
        <div className="flex flex-wrap items-center gap-4 px-6 py-5">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <h2 className="text-lg font-bold text-[#fcfcfc]">{title}</h2>
            <span className="rounded-2xl bg-[rgba(161,161,161,0.1)] px-2 py-0.5 text-xs font-bold text-[#38bdf8]">
              {totalCount}
            </span>
          </div>

          <label className="flex h-[43px] w-full max-w-[267px] items-center gap-2 rounded-lg border border-white/5 bg-[#1d2e46] px-4">
            <SearchIcon aria-hidden className="size-4 shrink-0" />
            <span className="sr-only">{searchLabel}</span>
            <input
              type="search"
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search..."
              className="w-full bg-transparent text-base text-white outline-none placeholder:text-[#9194a1]"
            />
          </label>

          <div ref={filterRef} className="relative w-full max-w-[275px]">
            <button
              type="button"
              aria-haspopup="listbox"
              aria-expanded={filterOpen}
              aria-controls={filterListId}
              onClick={() => setFilterOpen((open) => !open)}
              className="flex h-[43px] w-full items-center justify-between rounded-lg border border-white/5 bg-[#1d2e46] px-4 text-left text-base text-[#9194a1]"
            >
              <span>{getScoreFilterLabel(scoreFilter)}</span>
              <ChevronRightIcon
                aria-hidden
                className={cn(
                  "size-4 text-[#9194a1] transition-transform",
                  filterOpen && "rotate-[-90deg]",
                  !filterOpen && "rotate-90",
                )}
              />
            </button>
            {filterOpen ? (
              <ul
                id={filterListId}
                role="listbox"
                aria-label="Filter by TCI score"
                className="absolute right-0 z-20 mt-2 w-full overflow-hidden rounded-lg border border-white/[0.08] bg-[#091424] shadow-[0px_8px_24px_-8px_rgba(0,0,0,0.25)]"
              >
                {SCORE_FILTER_OPTIONS.map((option) => {
                  const isSelected = scoreFilter === option.value;

                  return (
                    <li key={option.value}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        className={cn(
                          "flex w-full items-center px-3 py-2.5 text-left text-[13px] text-white transition-colors",
                          isSelected
                            ? "bg-[#38bdf8] font-semibold"
                            : "font-medium hover:bg-white/5",
                        )}
                        onClick={() => {
                          onScoreFilterChange(option.value);
                          setFilterOpen(false);
                        }}
                      >
                        {option.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        </div>

        <div className="overflow-x-auto">
          <div className="min-w-[1286px]">
            <div className="flex items-center border-b border-white/5 bg-[#091424] px-6 py-3.5 text-xs font-bold uppercase tracking-[0.5px] text-[#9194a1]">
              <p className="w-[120px] shrink-0">Session ID</p>
              <p className="w-[84px] shrink-0">Region</p>
              <p className="w-[176px] shrink-0">Sector</p>
              <p className="w-[130px] shrink-0">Email</p>
              <p className="w-[160px] shrink-0">Organisation</p>
              <p className="w-[143px] shrink-0">Target EBITDA</p>
              <p className="min-w-0 flex-1">Total Drag</p>
              <p className="min-w-0 flex-1">TCI Score</p>
              <p className="w-[73px] shrink-0 text-right">Action</p>
            </div>

            {isLoading && leads.length === 0 ? (
              <p className="px-6 py-8 text-sm text-[#9194a1]">Loading leads…</p>
            ) : null}

            {!isLoading && leads.length === 0 ? (
              <p className="px-6 py-8 text-sm text-[#9194a1]">{emptyMessage}</p>
            ) : null}

            {leads.map((lead) => (
              <div
                key={lead.id}
                className="flex items-center border-b border-white/5 px-6 py-4"
                onClick={() => setOpenActionId(null)}
              >
                <div className="flex w-[120px] shrink-0 flex-col gap-1">
                  <p className="text-sm font-bold text-white">
                    {lead.sessionId}
                  </p>
                  <p className="text-xs text-[#9194a1]">{lead.relativeTime}</p>
                </div>
                <div className="w-[84px] shrink-0">
                  <p className="truncate text-sm font-medium text-white">
                    {lead.region}
                  </p>
                </div>
                <div className="w-[176px] shrink-0 pr-2">
                  <p
                    className="truncate text-base font-medium tracking-[-0.4px] text-white"
                    title={lead.sector}
                  >
                    {lead.sector}
                  </p>
                </div>
                <div className="w-[130px] shrink-0">
                  <p className="w-[100px] truncate text-xs text-[#9194a1]">
                    {lead.email}
                  </p>
                </div>
                <div className="w-[160px] shrink-0 pr-2">
                  <p
                    className="truncate text-sm font-medium text-white"
                    title={lead.organisationName}
                  >
                    {lead.organisationName}
                  </p>
                </div>
                <div className="w-[143px] shrink-0">
                  <p className="text-sm font-medium text-white">
                    {lead.targetEbitda}
                  </p>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#ef4444]">
                    {lead.totalDrag}
                  </p>
                </div>
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <p className="w-7 text-sm font-bold text-white">
                    {lead.tciScore}
                  </p>
                  <div className="h-1.5 w-[100px] overflow-hidden rounded-[3px] bg-[#1d2e46]">
                    <div
                      className={cn(
                        "h-full rounded-[20px]",
                        scoreBarClass(lead.scoreTone),
                      )}
                      style={{ width: `${Math.min(lead.tciScore, 100)}%` }}
                    />
                  </div>
                </div>
                <div className="relative flex w-[73px] shrink-0 items-center justify-end">
                  <button
                    type="button"
                    aria-label={`Actions for ${lead.sessionId}`}
                    aria-haspopup="menu"
                    aria-expanded={openActionId === lead.id}
                    className="rounded p-1 hover:bg-white/5"
                    onClick={(event) => {
                      event.stopPropagation();
                      setOpenActionId((current) =>
                        current === lead.id ? null : lead.id,
                      );
                    }}
                  >
                    <DotsVerticalIcon aria-hidden className="size-4" />
                  </button>
                  {openActionId === lead.id ? (
                    <div
                      role="menu"
                      className="absolute right-0 top-8 z-20 w-[197px] overflow-hidden rounded-lg border border-white/5 bg-[#0c1b2e] shadow-xl backdrop-blur-[8px]"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <button
                        type="button"
                        role="menuitem"
                        className="flex h-12 w-full items-center gap-2 border-b border-white/5 px-4 text-left text-sm leading-5 text-white hover:bg-white/5"
                        onClick={() => {
                          setOpenActionId(null);
                          setSelectedLead(lead);
                        }}
                      >
                        <EyeIcon aria-hidden className="size-4 shrink-0" />
                        <span className="min-w-0 flex-1">View</span>
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="flex h-12 w-full items-center gap-2 border-b border-white/5 px-4 text-left text-sm leading-5 text-[#ef4444] hover:bg-white/5"
                        onClick={() => void handleDelete(lead)}
                      >
                        <TrashIcon aria-hidden className="size-4 shrink-0" />
                        <span className="min-w-0 flex-1">Delete</span>
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="flex h-12 w-full items-center gap-2 px-4 text-left text-sm leading-5 text-white hover:bg-white/5"
                        onClick={() => {
                          setOpenActionId(null);
                          composeLeadEmail(lead.email);
                        }}
                      >
                        <MailIcon aria-hidden className="size-4 shrink-0" />
                        <span className="min-w-0 flex-1">Send Mail</span>
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {totalCount > 0 ? (
        <AdminPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          label={paginationLabel}
        />
      ) : null}
    </div>
  );
}

type AdminPaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  label?: string;
};

function AdminPagination({
  currentPage,
  totalPages,
  onPageChange,
  label = "Leads pagination",
}: AdminPaginationProps) {
  const pages = buildPageItems(currentPage, totalPages);

  return (
    <nav
      aria-label={label}
      className="flex items-center justify-end gap-6 pr-0 sm:pr-8"
    >
      <button
        type="button"
        aria-label="Previous page"
        disabled={currentPage <= 1}
        className="flex size-8 items-center justify-center rounded bg-[#132136] text-white disabled:opacity-40"
        onClick={() => onPageChange(currentPage - 1)}
      >
        <ChevronRightIcon aria-hidden className="size-4 rotate-180" />
      </button>

      <div className="flex items-center gap-2">
        {pages.map((page, index) =>
          page === "…" ? (
            <span
              key={`ellipsis-${index}`}
              className="flex size-8 items-center justify-center rounded bg-[#132136] text-sm font-semibold text-white"
            >
              …
            </span>
          ) : (
            <button
              key={page}
              type="button"
              aria-current={page === currentPage ? "page" : undefined}
              className={cn(
                "flex size-8 items-center justify-center rounded text-sm font-semibold text-white",
                page === currentPage ? "bg-[#38bdf8]" : "bg-[#132136]",
              )}
              onClick={() => onPageChange(page)}
            >
              {page}
            </button>
          ),
        )}
      </div>

      <button
        type="button"
        aria-label="Next page"
        disabled={currentPage >= totalPages}
        className="flex size-8 items-center justify-center rounded bg-white text-[#0c1b2e] disabled:opacity-40"
        onClick={() => onPageChange(currentPage + 1)}
      >
        <ChevronRightIcon aria-hidden className="size-4" />
      </button>
    </nav>
  );
}

function buildPageItems(
  currentPage: number,
  totalPages: number,
): Array<number | "…"> {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 2) {
    return [1, 2, "…", totalPages - 1, totalPages];
  }

  if (currentPage >= totalPages - 1) {
    return [1, 2, "…", totalPages - 1, totalPages];
  }

  return [1, currentPage, "…", totalPages - 1, totalPages];
}
